import { runInAction } from "mobx";
import { omitWallSurfaceMaterial } from "../components/Views/PropertyPanel/propertyPanelUtils";
import { floorplannerStore } from "../store/floorplannerStore";
import { renderStore } from "../store/renderStore";
import {
  AreaFillSymbolType,
  CircleSymbolType,
  FillPattern,
  isBackdropImageType,
  isAreaFillSymbolType,
  isSymbolType,
  isTextType,
  isVoidType,
  isWallType,
  PolygonSymbolType,
  RulerLineType,
  SingleLineType,
  SquareSymbolType,
  SvgType,
  SymbolType,
  TriangleSymbolType,
  WallFaceSide,
  WallType,
} from "../types/floorplannerTypes";
import { canonicalizeFillPattern } from "./fillPatterns";
import { clampFillOpacity, resolveFillOpacity } from "./fillOpacity";
import { bumpOpacityRemountEpoch } from "./opacityRemount";
import {
  clearWallFaceMaterials,
  resolveWallSideAppearance,
  upsertWallFaceMaterial,
  wallHasPerSideFaceMaterials,
  withSideOneMirror,
} from "./wallFaceSides";

/**
 * True when a wall has wallpaper / Ready / catalog surface material that
 * would hide fillColor in 3D until the material is cleared.
 */
const wallHasSurfaceMaterial = (wall: WallType): boolean =>
  Boolean(
    wall.threeD?.surfaceRef?.id ||
      wall.threeD?.materialId ||
      wall.threeD?.faceMaterials?.left?.surfaceRef?.id ||
      wall.threeD?.faceMaterials?.right?.surfaceRef?.id,
  );

/**
 * Drop whole-wall and per-side wallpaper / Ready materials so paint is visible.
 * Keeps fillColor face overrides and other threeD fields.
 */
const omitWallPaintCoveringMaterials = (
  threeD: WallType["threeD"] | undefined,
): WallType["threeD"] | undefined => {
  const withoutShared = omitWallSurfaceMaterial(threeD);
  if (!withoutShared?.faceMaterials) {
    return withoutShared;
  }
  const stripFaceSurface = (
    face: NonNullable<NonNullable<WallType["threeD"]>["faceMaterials"]>["left"],
  ) => {
    if (!face) return undefined;
    const { surfaceRef: _surfaceRef, ...rest } = face;
    return Object.keys(rest).length > 0 ? rest : undefined;
  };
  const left = stripFaceSurface(withoutShared.faceMaterials.left);
  const right = stripFaceSurface(withoutShared.faceMaterials.right);
  const faceMaterials =
    left || right ? { left, right } : undefined;
  const next = { ...withoutShared, faceMaterials };
  if (!faceMaterials) {
    delete next.faceMaterials;
  }
  return Object.keys(next).length > 0 ? next : undefined;
};

export type SelectableFillObject = WallType | SymbolType | SingleLineType | RulerLineType;
export type FillColorObject = WallType | SymbolType;
export type PatternFillObject =
  | WallType
  | AreaFillSymbolType
  | SquareSymbolType
  | CircleSymbolType
  | TriangleSymbolType
  | PolygonSymbolType
  | SvgType;

type FillUpdateOptions = {
  commitUndo?: boolean;
};

/**
 * Whether a wall currently exposes independent controls for its two broad faces.
 * Face data written before a whole-wall edit by a client without this feature
 * is ignored (see wallSideOneMirrorIsInSync).
 */
export const wallUsesDifferentSideColors = (wall: WallType): boolean =>
  wallHasPerSideFaceMaterials(wall);

/**
 * Enable or disable independent whole-side wall colors.
 */
export const setWallDifferentSideColors = (
  wallId: string,
  enabled: boolean,
): void => {
  const wall = floorplannerStore.wallsMap.get(wallId);
  if (!wall) return;

  floorplannerStore.beginUndoTransaction("update wall side colors");
  floorplannerStore.recordObjectForUndo(wall.id);

  if (enabled) {
    const defaultColor = wall.fillColor ?? floorplannerStore.fillColor;
    const sharedSurfaceRef = wall.threeD?.surfaceRef;
    // Start from a clean face map so stale Side 2 data cannot resurface, then
    // seed both faces from the whole-wall finish (color + material), matching
    // how fill color is duplicated. Materials can still diverge per side later.
    const baseWall: WallType = {
      ...wall,
      threeD: clearWallFaceMaterials(wall),
    };
    const faceSeed = {
      fillColor: defaultColor,
      ...(sharedSurfaceRef ? { surfaceRef: sharedSurfaceRef } : {}),
    };
    const withLeft = upsertWallFaceMaterial(baseWall, "left", faceSeed);
    const withBoth = upsertWallFaceMaterial(
      { ...baseWall, threeD: withLeft },
      "right",
      faceSeed,
    );
    // Whole-wall fields stay as the Side 1 mirror (they already equal Side 1).
    runInAction(() => {
      floorplannerStore.setObjectProperty(wall.id, "fillColor", defaultColor);
      floorplannerStore.setObjectProperty(
        wall.id,
        "threeD",
        withSideOneMirror(withBoth),
      );
    });
    renderStore.updateJointFillStyleForWall(
      wall.id,
      defaultColor,
      wall.fillPattern,
      wall.fillOpacity,
    );
  } else {
    // Collapsing two sides is necessarily lossy. Promote Side 1 to the shared
    // wall finish so disabling the mode does not unexpectedly restore a stale
    // color or material that was present before independent sides were enabled.
    const sideOneAppearance = resolveWallSideAppearance(wall, "left");
    const sharedThreeD = clearWallFaceMaterials({
      ...wall,
      threeD: withSideOneMirror({
        ...wall.threeD,
        faceMaterials: {
          ...wall.threeD?.faceMaterials,
          left: { surfaceRef: sideOneAppearance.surfaceRef },
        },
      }),
    });
    runInAction(() => {
      if (sideOneAppearance.fillColor !== undefined) {
        floorplannerStore.setObjectProperty(
          wall.id,
          "fillColor",
          sideOneAppearance.fillColor,
        );
      }
      floorplannerStore.setObjectProperty(wall.id, "threeD", sharedThreeD);
    });
    const restoredColor =
      sideOneAppearance.fillColor ?? wall.fillColor ?? floorplannerStore.fillColor;
    renderStore.updateJointFillStyleForWall(
      wall.id,
      restoredColor,
      wall.fillPattern,
      wall.fillOpacity,
    );
  }

  floorplannerStore.recordObjectChange(wall.id, "modify");
  floorplannerStore.pushToUndoStack();
  floorplannerStore.setDirty();
};

/**
 * Set the color for one full-length wall side.
 * Side 1 also updates wall.fillColor so the whole-wall mirror stays in sync.
 */
export const setWallSideColor = (
  wallId: string,
  side: WallFaceSide,
  value: string | number,
): void => {
  const wall = floorplannerStore.wallsMap.get(wallId);
  const color =
    typeof value === "string"
      ? Number.parseInt(value.replace("#", ""), 16)
      : value;
  if (!wall || !Number.isFinite(color)) return;

  floorplannerStore.beginUndoTransaction("update wall side color");
  floorplannerStore.recordObjectForUndo(wall.id);
  runInAction(() => {
    if (side === "left") {
      floorplannerStore.setObjectProperty(wall.id, "fillColor", color);
    }
    // Actively choosing a side color replaces wallpaper on that face so paint
    // is not hidden in 3D; withSideOneMirror keeps the whole-wall Side 1 sync.
    const updated = upsertWallFaceMaterial(wall, side, {
      fillColor: color,
      surfaceRef: null,
    });
    floorplannerStore.setObjectProperty(
      wall.id,
      "threeD",
      withSideOneMirror(updated),
    );
  });
  // Joint polygons are single-color; refresh from the side just painted so
  // corners do not keep a stale whole-wall fill.
  renderStore.updateJointFillStyleForWall(
    wall.id,
    color,
    wall.fillPattern,
    wall.fillOpacity,
  );
  floorplannerStore.recordObjectChange(wall.id, "modify");
  floorplannerStore.pushToUndoStack();
  floorplannerStore.setDirty();
};

export const numberToHexColor = (colorValue: number | string) => {
  if (typeof colorValue === "string") {
    const normalized = colorValue.trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(normalized)) return normalized;
    if (/^[0-9a-f]{6}$/.test(normalized)) return `#${normalized}`;
    if (normalized === "black") return "#000000";
    if (normalized === "white") return "#ffffff";
  }

  const numericColor =
    typeof colorValue === "number" && Number.isFinite(colorValue)
      ? colorValue
      : 0;
  let hexColor = numericColor.toString(16).padStart(6, "0").slice(-6);
  if (!hexColor.startsWith("#")) {
    hexColor = `#${hexColor}`;
  }
  return hexColor;
};

export const hexColorToNumber = (hexColor: string) => {
  if (hexColor.startsWith("#")) {
    hexColor = hexColor.slice(1);
  }
  return parseInt(hexColor, 16);
};

export const normalizeFillPattern = (pattern: FillPattern | undefined): FillPattern | undefined =>
  canonicalizeFillPattern(pattern);

export const getLiveSelectionObject = (
  selection: { id: string } | string,
): SelectableFillObject | undefined => {
  const id = typeof selection === "string" ? selection : selection.id;
  return (
    floorplannerStore.wallsMap.get(id) ||
    floorplannerStore.symbolsMap.get(id) ||
    floorplannerStore.singleLinesMap.get(id) ||
    floorplannerStore.rulerLinesMap.get(id)
  );
};

export const supportsFillColor = (
  object: SelectableFillObject | undefined,
): object is FillColorObject => {
  if (!object) return false;
  if (isWallType(object)) return true;
  return isSymbolType(object) &&
    !isTextType(object) &&
    !isBackdropImageType(object) &&
    !isVoidType(object);
};

export const supportsFillOpacity = (
  object: SelectableFillObject | undefined,
): object is FillColorObject => {
  if (!object) return false;
  if (isWallType(object)) return true;
  return (
    isSymbolType(object) &&
    [
      "door",
      "slidingDoor",
      "doubleDoor",
      "window",
      "circleStairs",
      "rectStairs",
      "turnStairs",
      "square",
      "circle",
      "triangle",
      "polygon",
      "svg",
      "areaFill",
      "smartTable",
      "bookShelf",
    ].includes(object.type)
  );
};

export const supportsPatternFill = (
  object: SelectableFillObject | undefined,
): object is PatternFillObject => {
  if (!object) return false;
  return (
    isWallType(object) ||
    (isSymbolType(object) &&
      (["square", "circle", "triangle", "polygon", "svg"].includes(
        object.type,
      ) ||
        isAreaFillSymbolType(object)))
  );
};

export const getFillColor = (object: FillColorObject): number =>
  object.fillColor !== undefined && object.fillColor !== null
    ? object.fillColor
    : floorplannerStore.fillColor;

export const getFillOpacity = (object: FillColorObject): number =>
  resolveFillOpacity(object);

export const getFillColorSummary = (objects: FillColorObject[]) => {
  const colors = objects.map(getFillColor);
  const firstColor = colors[0] ?? floorplannerStore.fillColor;
  return {
    color: numberToHexColor(firstColor),
    isMixed: colors.some((color) => color !== firstColor),
  };
};

export const getFillOpacitySummary = (objects: FillColorObject[]) => {
  const opacities = objects.map(getFillOpacity);
  const firstOpacity = opacities[0] ?? 1;
  return {
    opacity: firstOpacity,
    isMixed: opacities.some((opacity) => opacity !== firstOpacity),
  };
};

export const getFillPatternSummary = (objects: PatternFillObject[]) => {
  const patterns = objects.map((object) => normalizeFillPattern(object.fillPattern));
  const firstPattern = patterns[0];
  return {
    pattern: patterns.some((pattern) => pattern !== firstPattern) ? undefined : firstPattern,
    isMixed: patterns.length > 1 && patterns.some((pattern) => pattern !== firstPattern),
  };
};

export const updateFillColorForTargets = (
  sourceObject: FillColorObject | FillColorObject[] | { id: string } | { id: string }[],
  value: string | number,
  options: FillUpdateOptions = {},
): boolean => {
  const { commitUndo = true } = options;
  const color = typeof value === "string" ? hexColorToNumber(value) : value;
  if (!Number.isFinite(color)) return false;

  const sources = Array.isArray(sourceObject) ? sourceObject : [sourceObject];
  const targets = sources
    .map((source) => getLiveSelectionObject(source))
    .filter(supportsFillColor)
    .filter((target) => {
      // Paint replaces wallpaper even when fillColor is already the same hex —
      // otherwise NCS/Color apply looks like a no-op while wallpaper stays on top.
      if (isWallType(target) && wallHasSurfaceMaterial(target)) {
        return true;
      }
      return getFillColor(target) !== color;
    });

  if (targets.length === 0) return false;

  floorplannerStore.beginUndoTransaction("update fillColor");
  targets.forEach((target) => {
    floorplannerStore.recordObjectForUndo(target.id);
  });

  targets.forEach((target) => {
    if (isWallType(target)) {
      const liveWall = floorplannerStore.wallsMap.get(target.id);
      if (!liveWall) return;

      // Whole-wall paint: clear Side 1 / Side 2 overrides, and drop wallpaper /
      // Ready materials so the new fillColor is visible in 3D.
      const clearedFaceThreeD = clearWallFaceMaterials(liveWall);
      floorplannerStore.wallsMap.set(target.id, {
        ...liveWall,
        fillColor: color,
        threeD: wallHasSurfaceMaterial(liveWall)
          ? omitWallPaintCoveringMaterials(clearedFaceThreeD)
          : clearedFaceThreeD,
      });
      renderStore.updateJointFillStyleForWall(
        target.id,
        color,
        liveWall.fillPattern,
        liveWall.fillOpacity,
      );
      floorplannerStore.setDirty();
    } else {
      const liveSymbol = floorplannerStore.symbolsMap.get(target.id);
      floorplannerStore.updateSymbolProperty(target.id, "fillColor", color);
      floorplannerStore.updateSymbolProperty(target.id, "threeD", {
        ...liveSymbol?.threeD,
        fillColorOverride: true,
      });
    }
  });

  targets.forEach((target) => {
    floorplannerStore.recordObjectChange(target.id, "modify");
  });

  if (commitUndo) {
    floorplannerStore.pushToUndoStack();
  }

  return true;
};

export const updateFillOpacityForTargets = (
  sourceObject:
    | FillColorObject
    | FillColorObject[]
    | { id: string }
    | { id: string }[],
  value: number,
  options: FillUpdateOptions = {},
): boolean => {
  if (!Number.isFinite(value)) return false;

  const { commitUndo = true } = options;
  const opacity = clampFillOpacity(value);
  const sources = Array.isArray(sourceObject) ? sourceObject : [sourceObject];
  const targets = sources
    .map((source) => getLiveSelectionObject(source))
    .filter(supportsFillOpacity)
    .filter((target) => getFillOpacity(target) !== opacity);

  if (targets.length === 0) return false;

  floorplannerStore.beginUndoTransaction("update fillOpacity");
  targets.forEach((target) => {
    floorplannerStore.recordObjectForUndo(target.id);
  });

  targets.forEach((target) => {
    if (isWallType(target)) {
      floorplannerStore.setObjectProperty(target.id, "fillOpacity", opacity);
    } else {
      floorplannerStore.updateSymbolProperty(target.id, "fillOpacity", opacity);
    }
    // Same remount path as Restore — live slider updates must discard stale
    // Three.js materials or 0%/100% only look correct after a page refresh.
    bumpOpacityRemountEpoch(target.id);
  });

  targets.forEach((target) => {
    floorplannerStore.recordObjectChange(target.id, "modify");
  });

  if (commitUndo) {
    floorplannerStore.pushToUndoStack();
  }

  return true;
};

/**
 * Forces opacity back to fully opaque and remounts render meshes.
 *
 * Used when materials get stuck semi/fully transparent even though the slider
 * already reads 100% — a remount discards corrupted Three.js material clones.
 */
export const resetFillOpacityForTargets = (
  sourceObject:
    | FillColorObject
    | FillColorObject[]
    | { id: string }
    | { id: string }[],
): boolean => {
  const sources = Array.isArray(sourceObject) ? sourceObject : [sourceObject];
  const targets = sources
    .map((source) => getLiveSelectionObject(source))
    .filter(supportsFillOpacity);

  if (targets.length === 0) return false;

  floorplannerStore.beginUndoTransaction("reset fillOpacity");
  targets.forEach((target) => {
    floorplannerStore.recordObjectForUndo(target.id);
  });

  targets.forEach((target) => {
    if (isWallType(target)) {
      floorplannerStore.setObjectProperty(target.id, "fillOpacity", 1);
    } else {
      floorplannerStore.updateSymbolProperty(target.id, "fillOpacity", 1);
    }
    bumpOpacityRemountEpoch(target.id);
    floorplannerStore.recordObjectChange(target.id, "modify");
  });

  floorplannerStore.pushToUndoStack();
  return true;
};

export const updateFillPatternForTargets = (
  sourceObject: PatternFillObject | PatternFillObject[] | { id: string } | { id: string }[],
  value: FillPattern | undefined,
): boolean => {
  const nextPattern = normalizeFillPattern(value);
  const sources = Array.isArray(sourceObject) ? sourceObject : [sourceObject];
  const targets = sources
    .map((source) => getLiveSelectionObject(source))
    .filter(supportsPatternFill)
    .filter((target) => normalizeFillPattern(target.fillPattern) !== nextPattern);

  if (targets.length === 0) return false;

  floorplannerStore.beginUndoTransaction("update fillPattern");
  targets.forEach((target) => {
    floorplannerStore.recordObjectForUndo(target.id);
  });

  targets.forEach((target) => {
    if (isWallType(target)) {
      floorplannerStore.setObjectProperty(target.id, "fillPattern", nextPattern);
    } else {
      floorplannerStore.updateSymbolProperty(target.id, "fillPattern", nextPattern);
    }
  });

  targets.forEach((target) => {
    floorplannerStore.recordObjectChange(target.id, "modify");
  });

  floorplannerStore.pushToUndoStack();

  return true;
};
